import type { ExposeInterface } from './types';
import { Services } from "@ca.vmedia.tv/services";
import { type MedioGraphy, type Credit, type MedioGraphySection, type OnDemandItem } from "@ca.vmedia.tv/domains";
import type { AxiosError } from 'axios';
import { type CardData, CreditCardData, OnDemandCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import type { RowConfig } from "@/components/RowView/types";
import { ComponentName as SkeletonName } from "@/components/Skeleton/types";


export const useCastPageStore = defineStore('CastPage', (): ExposeInterface => {
    const _medioGraphy: Ref<MedioGraphy | null> = ref(null);
    const getMedioGraphy: ComputedRef<MedioGraphy | null> = computed(() => _medioGraphy.value);
    const fetchMedioGraphy = async (id: string): Promise<void> => {
        try {
            _medioGraphy.value = await Services.Instance.credits.getMedioGraphyVod(id);
        } catch (error) {
            _medioGraphy.value = null;
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsMedia: ComputedRef<RowConfig[]> = computed(() => {
        if (!_medioGraphy.value?.mediographySections || _medioGraphy.value?.mediographySections.length === 0) {
            return [];
        }

        const lines: RowConfig[] = [];
        _medioGraphy.value.mediographySections.forEach((section, idx): MedioGraphySection => {
                lines.push({
                    keyData: AppConstants.shelfKeys.relatedCards,
                    title: section.category?.name ?? null,
                    ref: AppConstants.shelfKeys.relatedCards,
                    lineId: `${AppConstants.shelfKeys.relatedCards}_${idx}`,
                    skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                    cardItems: section.items ? section.items.map((item: OnDemandItem) => new OnDemandCardData(item)) : [],
                });
            return section;
        });

        return lines;
    });

    const _castRelated: Ref<Credit[] | null> = ref(null);
    const fetchCastRelated = async (id: string): Promise<void> => {
        try {
            _castRelated.value = await Services.Instance.credits.searchRelatedCredits(id);
        } catch (error) {
            _castRelated.value = [];
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsCastRelated: ComputedRef<CardData[] | null> = computed(() => {
        const data = _castRelated.value;

        if (data === null) {
            return null;
        }

        if (data.length === 0) {
            return [];
        }

        return data.map((item) => new CreditCardData(item));
    }); 
    
    const fetchCastInfo = (id: string): void => {
        void fetchMedioGraphy(id);
        void fetchCastRelated(id);
    };

    const _viewedCastDetailItems: Ref<Credit[] | null> = ref(null);
    const viewedCastDetailItems = computed((): Credit[] | null => _viewedCastDetailItems.value);
    const setViewedCastDetailItems = (item: Credit): void => {
        if (!_viewedCastDetailItems.value) {
            _viewedCastDetailItems.value = [item];
        } else {
            _viewedCastDetailItems.value.push(item);
        }
    };

    const _currentCastItemDetail: Ref<Credit | null> = ref(null);
    const getCurrentCastItemDetail: ComputedRef<Credit | null> = computed(() => _currentCastItemDetail.value);
    const setCurrentCastItemDetail = (id: string): void => {
        if (id && viewedCastDetailItems.value && viewedCastDetailItems.value.length > 0) {
            _currentCastItemDetail.value = viewedCastDetailItems.value.find((item: Credit) => item.person?.id === Number(id)) ?? null;
        }
    };

    const _counterRows: Ref<number> = ref(AppConstants.DetailCounterLines);
    const getCounterLinesDetailMovie: ComputedRef<number> = computed(() => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.DetailCounterLines;
    };
    const increaseCounterLinesCastDetail = (): void => {
        _counterRows.value += 1;
    };

    const clearCastMediaDetail = (): void => {
        _medioGraphy.value = null;
        _castRelated.value = null;
    };
    const clearAllCastPage = (): void => {
        _clearCounter();
        _medioGraphy.value = null;
        _castRelated.value = null;
        _currentCastItemDetail.value = null;
        _viewedCastDetailItems.value = null;
    };

    return {
        getMedioGraphy,
        fetchMedioGraphy,
        getCardsCastRelated,
        fetchCastRelated,
        fetchCastInfo,
        clearAllCastPage,
        clearCastMediaDetail,
        viewedCastDetailItems,
        setViewedCastDetailItems,
        getCurrentCastItemDetail,
        setCurrentCastItemDetail,
        getCounterLinesDetailMovie,
        increaseCounterLinesCastDetail,
        getCardsMedia
    };
});
